import { useState } from "react";
import { AlertTriangle, Check, Circle, Clock3, Paperclip, ReceiptText } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";

const STATE_STYLES = {
  complete: "bg-[#fcfefd] border-gray-300 border-l-emerald-500",
  current: "bg-white border-gray-400 border-l-amber-500 shadow-[0_5px_16px_rgba(16,19,21,0.09)]",
  future: "bg-gray-50/90 border-gray-200 border-l-gray-300 opacity-70",
  overdue: "bg-red-50/70 border-red-300 border-l-red-500 shadow-sm",
  na: "bg-gray-50 border-gray-200 border-l-gray-300 opacity-45",
};

const STANDARD_STYLES = {
  complete: "bg-white border-gray-300",
  current: "bg-white border-gray-900 ring-1 ring-gray-900 shadow-md",
  future: "bg-gray-50 border-gray-200 opacity-55",
  overdue: "bg-red-50/60 border-red-400",
  na: "bg-gray-50 border-gray-200 opacity-40",
};

function StageIcon({ state, icon: Icon }) {
  const base = "flex h-9 w-9 shrink-0 items-center justify-center rounded-md border shadow-sm";
  const Glyph = Icon || (state === "complete" ? Check : state === "current" ? Clock3 : state === "overdue" ? AlertTriangle : Circle);
  if (state === "complete") return <span className={`${base} border-emerald-200 bg-emerald-50 text-emerald-700`}><Glyph size={17} strokeWidth={1.9} /></span>;
  if (state === "current") return <span className={`${base} border-amber-200 bg-amber-50 text-amber-700`}><Glyph size={17} strokeWidth={1.9} /></span>;
  if (state === "overdue") return <span className={`${base} border-red-200 bg-red-50 text-red-700`}><Glyph size={17} strokeWidth={1.9} /></span>;
  return <span className={`${base} border-gray-200 bg-white text-gray-400`}><Glyph size={16} strokeWidth={1.7} /></span>;
}

export function Connector({ active, tall, appearance = "standard" }) {
  if (appearance !== "electrical") {
    return <div className={`mx-auto w-px ${tall ? "h-8" : "h-5"} ${active ? "bg-gray-900" : "bg-gray-300"}`} />;
  }
  return (
    <div className={`relative mx-auto ${tall ? "h-10" : "h-7"} w-[2px] ${active ? "bg-emerald-500" : "bg-gray-300"}`} aria-hidden="true">
      <span className={`absolute -bottom-0.5 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full ${active ? "bg-emerald-500" : "bg-gray-300"}`} />
    </div>
  );
}

export default function FlowNode({ title, lines = [], status, tone, state = "future", onClick, testId, attachments = [], onViewDoc, appearance = "standard", icon, stageType = "Lifecycle Stage", costTile = null }) {
  const [showList, setShowList] = useState(false);
  const electrical = appearance === "electrical";

  const clickAttachment = (e) => {
    e.stopPropagation();
    if (attachments.length === 1) onViewDoc && onViewDoc(attachments[0]);
    else setShowList((s) => !s);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={testId}
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick && onClick()}
      className={`relative block w-full cursor-pointer text-left transition-[border-color,box-shadow,opacity,transform,background-color] duration-200 hover:-translate-y-0.5 hover:opacity-100 ${
        electrical
          ? `min-h-[108px] rounded-lg border border-l-4 px-4 py-4 hover:shadow-[0_7px_18px_rgba(16,19,21,0.09)] ${STATE_STYLES[state]}`
          : `rounded-sm border px-3 py-2.5 hover:shadow-md ${STANDARD_STYLES[state]}`
      }`}
    >
      <div className={`flex items-start justify-between ${electrical ? "gap-3" : "gap-2"}`}>
        <div className={`flex min-w-0 items-start ${electrical ? "gap-3" : "gap-0"}`}>
          {electrical && <StageIcon state={state} icon={icon} />}
          <div className="min-w-0">
            {electrical && <div className="mb-1 text-[9px] font-semibold uppercase text-gray-400" style={{ letterSpacing: "0.12em" }}>{stageType}</div>}
            <div className={`${electrical ? "text-sm leading-snug" : "text-[13px] leading-tight"} font-heading font-bold text-gray-950`}>{title}</div>
          </div>
        </div>
        <div className={`flex shrink-0 ${costTile ? "flex-col items-end gap-1.5" : "items-center gap-1.5"}`}>
          <div className="flex items-center gap-1.5">
          {attachments.length > 0 && (
            <button
              type="button"
              data-testid={testId ? `${testId}-attachment` : "node-attachment"}
              onClick={clickAttachment}
              title={attachments.length === 1 ? `View ${attachments[0].filename}` : `${attachments.length} attached documents`}
              className="relative flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 transition-[color,background-color,border-color] duration-150 hover:border-[#b9a4cc] hover:bg-[#f3eff7] hover:text-[#604184]"
              aria-label={attachments.length === 1 ? `View ${attachments[0].filename}` : `View ${attachments.length} attached documents`}
            >
              <Paperclip size={12} strokeWidth={2} />
              {attachments.length > 1 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-gray-900 px-1 text-[8px] font-bold text-white">{attachments.length}</span>}
            </button>
          )}
          <StatusBadge status={status} tone={tone} />
          </div>
          {costTile && (
            <div data-testid={testId ? `${testId}-external-cost` : "node-external-cost"} className="rounded-md border border-orange-200 bg-orange-50 px-2 py-1 text-right shadow-sm">
              <div className="flex items-center justify-end gap-1 text-[8px] font-semibold uppercase text-orange-600" style={{ letterSpacing: "0.1em" }}>
                <ReceiptText size={9} strokeWidth={1.8} /> VQR Cost
              </div>
              <div className="font-mono-data text-[11px] font-semibold leading-tight text-gray-950">{costTile}</div>
            </div>
          )}
        </div>
      </div>
      {lines.filter(Boolean).length > 0 && (
        <div className={`${electrical ? "ml-12 mt-3 border-t border-gray-200 pt-2.5" : "mt-1.5"} space-y-1`}>
          {lines.filter(Boolean).map((line, i) => (
            <div key={i} className={`${electrical ? "text-[10px] leading-relaxed text-gray-600" : "text-[11px] leading-tight text-gray-500"} font-mono-data`}>{line}</div>
          ))}
        </div>
      )}
      {state === "current" && electrical && (
        <div className="ml-12 mt-3 flex items-center gap-2 border-t border-amber-200 pt-2.5 text-[9px] font-bold uppercase text-amber-800" style={{ letterSpacing: "0.1em" }}>
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Current Operational Stage
        </div>
      )}
      {state === "current" && !electrical && (
        <div className="mt-1.5 text-[10px] font-bold uppercase text-gray-900" style={{ letterSpacing: "0.15em" }}>◈ Current Stage</div>
      )}
      {showList && attachments.length > 1 && (
        <div data-testid={`${testId}-attachment-list`} className="absolute right-2 top-12 z-20 min-w-[230px] rounded-md border border-gray-200 bg-white py-1.5 shadow-[0_14px_34px_rgba(16,19,21,0.16)]" onClick={(e) => e.stopPropagation()}>
          {attachments.map((a) => (
            <button
              key={a.id}
              type="button"
              data-testid={`${testId}-attachment-${a.id}`}
              className="flex w-full items-center gap-2 truncate px-3 py-2 text-left text-xs text-gray-700 transition-colors duration-150 hover:bg-gray-100"
              onClick={(e) => { e.stopPropagation(); setShowList(false); onViewDoc && onViewDoc(a); }}
            >
              <Paperclip size={11} strokeWidth={2} className="shrink-0 text-gray-400" />
              {a.filename}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}